<template>
    <div>
        <div class="rma-board-bar">
            <div class="rma-board-legend" aria-label="Legenda">
                <span v-for="tone in tones" :key="tone.value" class="rma-board-legend__item">
                    <span class="rma-doc" :class="`rma-doc--${tone.value}`"></span>{{ tone.text }}
                </span>
            </div>
            <v-spacer></v-spacer>
            <v-select v-model="filter" :items="filterItems" dense outlined hide-details class="rma-board-bar__select" :menu-props="{ offsetY: true }"
                prepend-inner-icon="mdi-account-tie-outline" aria-label="Responsável"></v-select>
        </div>

        <div v-if="loading && !rows.length" class="ui-card"><ui-empty icon="mdi-account-group-outline" text="Carregando..." /></div>
        <div v-else-if="!groups.length" class="ui-card"><ui-empty icon="mdi-account-group-outline" text="Nenhum caso para este filtro" /></div>

        <section v-for="group in groups" :key="group.key" class="ui-card rma-board-group">
            <header class="rma-board-group__header">
                <span class="rma-board-group__avatar" :class="{ 'rma-board-group__avatar--none': !group.id }">{{ initials(group.name) }}</span>
                <div style="min-width: 0;">
                    <div class="rma-board-group__name">{{ group.name }}<span v-if="group.id && group.id === me" class="ui-muted"> (você)</span></div>
                    <div class="ui-small ui-muted">
                        {{ group.rows.length }} {{ group.rows.length === 1 ? "caso" : "casos" }}
                        · {{ group.done }} juntados · {{ group.pending }} pendentes
                    </div>
                </div>
                <v-spacer></v-spacer>
                <div class="rma-board-group__percent">
                    <v-progress-linear :value="group.percent" height="6" rounded color="success" background-color="#eef0f3" style="width: 120px;"></v-progress-linear>
                    <span>{{ group.percent }}%</span>
                </div>
            </header>

            <button v-for="row in group.rows" :key="row.client_id" type="button" class="rma-board-row" @click="$emit('open', row)">
                <div class="rma-board-row__case">
                    <div class="ui-cell-main text-truncate">{{ row.client_name }}</div>
                    <div class="ui-cell-sub">
                        <span v-if="!row.active">RMA desativado · </span>{{ row.status_label }}<span v-if="row.overdue" style="color: #b91c1c;"> · prazo encerrado</span>
                        <span v-if="row.pending_drafts" style="color: #8a5a00;"> · {{ row.pending_drafts === 1 ? "1 e-mail aguardando revisão" : `${row.pending_drafts} e-mails aguardando revisão` }}</span>
                    </div>
                </div>
                <div v-if="row.opened && row.documents.length" class="rma-board-row__docs">
                    <span v-for="company in companiesOf(row)" :key="company.id" class="rma-board-row__company">
                        <span v-for="doc in company.documents" :key="`${company.id}-${doc.type_id}`" class="rma-doc" :class="`rma-doc--${doc.tone}`"
                            :title="docTitle(doc, row)"></span>
                    </span>
                </div>
                <div v-else class="rma-board-row__docs ui-small ui-muted">Competência não aberta</div>
                <div class="rma-board-row__percent">{{ percentLabel(row) }}</div>
                <v-icon small class="ui-cell-chevron">mdi-chevron-right</v-icon>
            </button>
        </section>
    </div>
</template>

<script>
// Painel por responsavel (Painel RMA): os casos agrupados pelo responsavel do
// RMA, com um quadro por documento da competencia. Verde = juntado, amarelo =
// solicitado e nao enviado, cinza = nao solicitado no caso ou "nao se aplica".
const TONES = [
    { value: "done", text: "Juntado" },
    { value: "pending", text: "Solicitado e não enviado" },
    { value: "na", text: "Não solicitado (N/A)" },
];

const STATUS_TEXT = { sent: "enviado", accepted: "conferido", pending: "pendente", returned: "devolvido", not_applicable: "não se aplica" };

export default {
    name: "RmaResponsibleBoard",

    props: {
        rows: { type: Array, default: () => [] },
        me: { type: Number, default: null },
        loading: { type: Boolean, default: false },
    },

    data: () => ({ filter: "all", tones: TONES, touched: false }),

    computed: {
        responsibles() {
            const map = new Map();
            this.rows.forEach((row) => { if (row.responsible) map.set(row.responsible.id, row.responsible.name); });
            return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => String(a.name).localeCompare(String(b.name), "pt-BR"));
        },
        filterItems() {
            const items = [{ value: "all", text: "Todos os responsáveis" }];
            this.responsibles.forEach((person) => items.push({ value: person.id, text: person.id === this.me ? `${person.name} (você)` : person.name }));
            if (this.rows.some((row) => !row.responsible)) items.push({ value: "none", text: "Sem responsável" });
            return items;
        },
        groups() {
            const map = new Map();
            this.rows.forEach((row) => {
                const key = row.responsible ? `r${row.responsible.id}` : "none";
                if (this.filter === "none" && row.responsible) return;
                if (typeof this.filter === "number" && (!row.responsible || row.responsible.id !== this.filter)) return;
                if (!map.has(key)) map.set(key, { key, id: row.responsible ? row.responsible.id : null, name: row.responsible ? row.responsible.name : "Sem responsável", rows: [] });
                map.get(key).rows.push(row);
            });
            return [...map.values()]
                .map((group) => {
                    const docs = group.rows.reduce((list, row) => list.concat(row.documents || []), []);
                    const done = docs.filter((doc) => doc.tone === "done").length;
                    const pending = docs.filter((doc) => doc.tone === "pending").length;
                    return { ...group, done, pending, percent: done + pending ? Math.round((100 * done) / (done + pending)) : 0 };
                })
                .sort((a, b) => {
                    if (!a.id !== !b.id) return a.id ? -1 : 1;
                    if (a.id === this.me || b.id === this.me) return a.id === this.me ? -1 : 1;
                    return String(a.name).localeCompare(String(b.name), "pt-BR");
                });
        },
    },

    watch: {
        // Abre filtrado no proprio usuario quando ele e responsavel por algum caso.
        responsibles(list) {
            if (this.touched) return;
            if (this.me && list.some((person) => person.id === this.me)) this.filter = this.me;
        },
        filter() {
            this.touched = true;
        },
    },

    methods: {
        initials(name) {
            const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
            if (!parts.length) return "?";
            return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
        },

        companiesOf(row) {
            const map = new Map();
            row.documents.forEach((doc) => {
                if (!map.has(doc.company_id)) map.set(doc.company_id, { id: doc.company_id, name: doc.company_name, documents: [] });
                map.get(doc.company_id).documents.push(doc);
            });
            return [...map.values()];
        },

        docTitle(doc, row) {
            const multi = this.companiesOf(row).length > 1;
            const state = doc.status ? STATUS_TEXT[doc.status] || doc.status : "não solicitado";
            return `${String(doc.sort_order).padStart(2, "0")} · ${doc.name}${multi ? ` · ${doc.company_name}` : ""} — ${state}`;
        },

        percentLabel(row) {
            const docs = row.documents || [];
            const done = docs.filter((doc) => doc.tone === "done").length;
            const pending = docs.filter((doc) => doc.tone === "pending").length;
            return done + pending ? `${Math.round((100 * done) / (done + pending))}%` : "—";
        },
    },
};
</script>

<style scoped lang="scss">
@import "../../assets/scss/ui/tokens";

.rma-board-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px 16px;
    margin-bottom: 12px;
}

.rma-board-bar__select {
    max-width: 300px;
    background: $ui-bg;
}

.rma-board-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 18px;
    font-size: 12.5px;
    color: $ui-muted;
}

.rma-board-legend__item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.rma-doc {
    display: inline-block;
    width: 14px;
    height: 14px;
    border-radius: 3px;
    flex: none;

    &--done {
        background: #2fb37e;
    }

    &--pending {
        background: #f6c744;
    }

    &--na {
        background: #c3c7d0;
    }
}

.rma-board-group {
    padding: 0;
    margin-bottom: 14px;
    overflow: hidden;
}

.rma-board-group__header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 18px;
    border-bottom: 1px solid $ui-border-soft;
    background: $ui-bg-muted;
}

.rma-board-group__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: $ui-accent-soft;
    color: $ui-accent;
    font-weight: 600;
    font-size: 13px;
    flex: none;

    &--none {
        background: #eef0f3;
        color: $ui-muted;
    }
}

.rma-board-group__name {
    font-weight: 600;
    color: $ui-fg;
}

.rma-board-group__percent {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 13px;
}

.rma-board-row {
    display: grid;
    grid-template-columns: minmax(180px, 260px) 1fr 48px 20px;
    align-items: center;
    gap: 16px;
    width: 100%;
    padding: 10px 18px;
    background: transparent;
    border: 0;
    border-top: 1px solid $ui-border-soft;
    text-align: left;
    font: inherit;
    color: inherit;
    cursor: pointer;

    &:first-of-type {
        border-top: 0;
    }

    &:hover {
        background: $ui-bg-muted;
    }

    &:focus-visible {
        outline: 2px solid $ui-ring;
        outline-offset: -2px;
    }
}

.rma-board-row__case {
    min-width: 0;
}

.rma-board-row__docs {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 12px;
    min-width: 0;
}

.rma-board-row__company {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 3px;
}

.rma-board-row__percent {
    font-size: 12.5px;
    font-weight: 600;
    text-align: right;
    color: $ui-muted;
}

@media (max-width: 700px) {
    .rma-board-row {
        grid-template-columns: 1fr 40px 16px;
    }

    .rma-board-row__docs {
        grid-column: 1 / -1;
        grid-row: 2;
    }
}
</style>
